import React, { useEffect, useMemo, useState } from 'react';
import styles from './TriageBookingModal.module.css';
import DatePicker from '../DatePicker/DatePicker';
import { useDispatch, useSelector } from '../../redux/store';
import {
  fetchBookingBusinesses,
  fetchBookingServices,
  fetchBookingStaff,
  fetchBookingSlots,
  createBookingAppointment,
  cancelBookingAppointment,
  fetchBookingAppointment,
} from '../../redux/slices/bookings';
import { useSnackbar } from 'notistack';
import TriageAppointmentsModal from './TriageAppointmentsModal';

function parseISODurationToMinutes(isoDuration) {
  // Expected like 'PT15M', 'PT1H30M'
  if (typeof isoDuration !== 'string' || !isoDuration.startsWith('PT')) return 15;
  const hoursMatch = isoDuration.match(/(\d+)H/);
  const minutesMatch = isoDuration.match(/(\d+)M/);
  const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
  const minutes = minutesMatch ? parseInt(minutesMatch[1]) : 0;
  const total = hours * 60 + minutes;
  return total > 0 ? total : 15;
}

function minutesToTimeLabel(totalMinutes) {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const hh = String(h).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  return `${hh}:${mm}`;
}

function buildSlotsForDay(date, durationMinutes, startHour = 9, endHour = 17) {
  if (!date) return [];
  const startTotal = startHour * 60;
  const endTotal = endHour * 60;
  const slots = [];
  for (let t = startTotal; t <= endTotal - durationMinutes; t += Math.max(durationMinutes, 15)) {
    slots.push(minutesToTimeLabel(t));
  }
  return slots;
}

/**
 * Internal helper to parse triage booking titles into project number and topic.
 * Format: "Triage: [ProjectNumber] - [Address] | [Topic] - [Staff/Role]"
 */
const parseTriageTitle = (title, staffList = []) => {
  let projectNumber = '';
  let topic = '';

  if (!title || typeof title !== 'string') return { projectNumber, topic };

  // 1. Remove "Triage: " prefix
  let cleanTitle = title.replace(/^Triage:\s*/i, '').trim();

  // 2. Handle the "|" separator for Project/Address vs Topic
  if (cleanTitle.includes('|')) {
    const pipeParts = cleanTitle.split('|');
    const leftPart = pipeParts[0].trim();
    const rightPart = pipeParts[1].trim();

    // Extract project number from left part (e.g., "8930 - Address")
    const pMatch = leftPart.match(/^(\d+)/);
    if (pMatch) projectNumber = pMatch[1];

    topic = rightPart;
  } else {
    // No "|", the whole thing might be topic, but check for project number at start
    topic = cleanTitle;
    const pMatch = cleanTitle.match(/^(\d+)/);
    if (pMatch) {
      projectNumber = pMatch[1];
      // If it looks like "8930 - Topic", strip the prefix from the topic field
      topic = topic.replace(/^\d+\s*-\s*/, '').trim();
    }
  }

  // 3. Strip staff/role suffix if it looks like one
  // Logic: if it ends with " - [StaffName]" or " - [Role]"
  if (topic.includes(' - ')) {
    const dashParts = topic.split(' - ');
    if (dashParts.length > 1) {
      const potentialSuffix = dashParts[dashParts.length - 1].trim();
      
      // Check against staff list if provided
      const isStaff = staffList.some(s => 
        (s.displayName && s.displayName.toLowerCase() === potentialSuffix.toLowerCase()) ||
        (s.name && s.name.toLowerCase() === potentialSuffix.toLowerCase()) ||
        (s.role && s.role.toLowerCase() === potentialSuffix.toLowerCase())
      );

      // Common roles as fallback
      const commonRoles = ['DevOps Engineer', 'Engineer', 'Admin', 'Director', 'Manager'];
      const isCommonRole = commonRoles.some(r => r.toLowerCase() === potentialSuffix.toLowerCase());

      if (isStaff || isCommonRole) {
        // Reconstruct topic without the last dash part
        topic = dashParts.slice(0, -1).join(' - ').trim();
      }
    }
  }

  return { projectNumber, topic };
};

export default function TriageBookingModal({ isVisible, onClose, jobNumber, projectName, defaultName, defaultEmail }) {
  const dispatch = useDispatch();
  const bookings = useSelector((s) => s.bookings);
  const { enqueueSnackbar } = useSnackbar();
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [serviceDurationMins, setServiceDurationMins] = useState(15);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState(() => ({ startDate: new Date().toISOString() }));
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [name, setName] = useState(defaultName || '');
  const [email, setEmail] = useState(defaultEmail || '');
  const [notes, setNotes] = useState('');
  const [projectNumberInput, setProjectNumberInput] = useState(jobNumber || '');
  const [showManageTriage, setShowManageTriage] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // { appointmentId, businessId, projectNumber? }

  const services = bookings.servicesByBusiness[selectedBusinessId] || [];
  const staff = bookings.staffByBusiness[selectedBusinessId] || [];
  const dateObj = useMemo(() => {
    if (selectedDate && typeof selectedDate.startDate === 'string') {
      const d = new Date(selectedDate.startDate);
      if (!isNaN(d.getTime())) return d;
    }
    if (selectedDate instanceof Date) return selectedDate;
    return new Date();
  }, [selectedDate]);
  // Build date string in Europe/London time zone to ensure UK date is used
  const { year: ukYear, month: ukMonth, day: ukDay } = useMemo(() => {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(dateObj);
    const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
    return { year: map.year, month: map.month, day: map.day };
  }, [dateObj]);
  const dateStr = `${ukYear}-${ukMonth}-${ukDay}`;
  const slotsKey = selectedStaffId
    ? `${selectedBusinessId}|${dateStr}|${selectedServiceId}|${selectedStaffId}`
    : '';
  const slots = slotsKey ? (bookings.slotsByKey[slotsKey] || []) : [];
  const now = new Date();
  const isSameDayAsSelected = now.getFullYear() === dateObj.getFullYear() && now.getMonth() === dateObj.getMonth() && now.getDate() === dateObj.getDate();
  const bookingBufferMinutes = 30; // half an hour buffer

  useEffect(() => {
    if (!isVisible) return;
    setInitialLoading(true);
    dispatch(fetchBookingBusinesses()).then((action) => {
      const bizList = action?.payload || [];
      const triageBusiness = bizList.find(b => (b.displayName || '').toLowerCase().includes('triage')) || bizList[0];
      const businessId = triageBusiness?.id || bizList[0]?.id || '';
      setSelectedBusinessId(businessId);
      if (businessId) {
        dispatch(fetchBookingServices(businessId)).then((svcAction) => {
          const svcList = svcAction?.payload?.services || [];
          const triageService = svcList.find(s => (s.displayName || '').toLowerCase().includes('triage')) || svcList[0];
          setSelectedServiceId(triageService?.id || '');
          setServiceDurationMins(parseISODurationToMinutes(triageService?.defaultDuration));
        });
        dispatch(fetchBookingStaff(businessId));
      }
    });
  }, [isVisible, dispatch]);

  useEffect(() => {
    const svc = services.find(s => s.id === selectedServiceId);
    if (svc) setServiceDurationMins(parseISODurationToMinutes(svc.defaultDuration));
  }, [selectedServiceId, services]);

  useEffect(() => {
    if (!selectedBusinessId || !selectedServiceId || !dateStr) return;
    if (!selectedStaffId) { setInitialLoading(false); setSlotsLoading(false); return; }
    const staffIds = [selectedStaffId];
    setSlotsLoading(true);
    dispatch(fetchBookingSlots({
      businessId: selectedBusinessId,
      body: {
        serviceId: selectedServiceId,
        durationMinutes: serviceDurationMins,
        date: dateStr,
        staffIds,
        timeZone: 'Europe/London',
      }
    })).finally(() => {
      setInitialLoading(false);
      setSlotsLoading(false);
    });
  }, [dispatch, selectedBusinessId, selectedServiceId, selectedStaffId, dateStr, serviceDurationMins]);

  // Determine effective project number: prefer jobNumber prop (from project profile), else optional input
  const effectiveProjectNumber = jobNumber || (projectNumberInput && projectNumberInput.trim() !== '' ? projectNumberInput.trim() : null);
  const requiresProjectNumber = !!jobNumber; // if opened from project profile, project number must be present

  const emailValid = useMemo(() => /.+@.+\..+/.test(email || ''), [email]);
  const canBook = (name?.trim().length > 0) && emailValid && selectedBusinessId && selectedServiceId && selectedTime && selectedDate && (notes?.trim().length > 0) && (!requiresProjectNumber || !!effectiveProjectNumber);

  const handleStartReschedule = async (appt) => {
    if (!appt) return;
    const appointmentId = appt?.id || appt?.appointmentId;
    let businessId = appt?.businessId || appt?.business?.id || appt?.business_id;

    // If businessId looks like an email (contains @), try to find the actual business ID
    if (businessId && businessId.includes('@')) {
      const businessName = appt?.businessName || businessId;
      const matchingBusiness = bookings.businesses.find(b =>
        (b.displayName || '').toLowerCase() === businessName.toLowerCase() ||
        (b.name || '').toLowerCase() === businessName.toLowerCase() ||
        (b.id || '').toLowerCase() === businessName.toLowerCase()
      );
      if (matchingBusiness) {
        businessId = matchingBusiness.id;
        console.log('Mapped business name/email to business ID:', businessId);
      } else {
        console.log('Could not find matching business for:', businessName);
      }
    }

    if (!appointmentId || !businessId) {
      const errorMsg = `Missing appointment details to reschedule. Found appointmentId: ${appointmentId}, businessId: ${businessId}`;
      console.error(errorMsg);
      enqueueSnackbar(errorMsg, { variant: 'error' });
      return;
    }

    // Try to fetch the full appointment details to get the definitive calendar title
    let fullAppt = null;
    try {
      const action = await dispatch(fetchBookingAppointment({ businessId, appointmentId }));
      if (action.type.endsWith('fulfilled')) {
        fullAppt = action.payload;
      }
    } catch (err) {
      console.warn('Failed to fetch full appointment details, falling back to provided object.', err);
    }

    const targetAppt = fullAppt || appt;

    // Prioritize definitive title if we have it to extract project number and clean topic
    const calendarTitle = targetAppt?.calendarTitle || targetAppt?.serviceName || targetAppt?.service?.name || '';
    const { projectNumber: extractedProject, topic: extractedTopic } = parseTriageTitle(calendarTitle, staff);

    const projectNumber = targetAppt?.projectNumber || targetAppt?.project_no || targetAppt?.job_no || extractedProject;
    const customerName = targetAppt?.customerName || targetAppt?.customer_name || targetAppt?.customers?.[0]?.name || targetAppt?.customers?.[0]?.displayName;
    const customerEmail = targetAppt?.customerEmail || targetAppt?.customer_email || targetAppt?.customers?.[0]?.emailAddress?.address;

    // If we extracted a clean topic from the title, use it; otherwise fall back to existing notes fields
    const appointmentNotes = extractedTopic || targetAppt?.notes || targetAppt?.description || targetAppt?.topic || targetAppt?.serviceNotes || '';

    setRescheduleTarget({
      appointmentId,
      businessId,
      projectNumber: projectNumber ? String(projectNumber) : null,
      calendarTitle,
    });

    // Pre-fill project number where appropriate (only if modal not already bound to a jobNumber prop)
    if (!jobNumber && projectNumber) {
      setProjectNumberInput(String(projectNumber));
    }
    // Keep name/email aligned with original appointment, if present
    if (customerName) setName(customerName);
    if (customerEmail) setEmail(customerEmail);
    // Pre-fill notes/topic from the original appointment (now cleaned if extracted from title)
    if (appointmentNotes) setNotes(appointmentNotes);

    setShowManageTriage(false);
  };

  const handleDateChange = (ranges) => {
    setSelectedDate(ranges);
    setSelectedTime('');
    setSelectedSlot(null);
  };

  const handleBook = async () => {
    if (!canBook) return;
    const start = selectedSlot ? new Date(selectedSlot.start) : null;
    const end = selectedSlot ? new Date(selectedSlot.end) : null;
    const effectiveProjectNumber = jobNumber || (projectNumberInput && projectNumberInput.trim() !== '' ? projectNumberInput.trim() : null);
    const payload = {
      serviceId: selectedServiceId,
      customerName: name,
      customerEmail: email,
      start: start?.toISOString(),
      end: end?.toISOString(),
      staffMemberIds: selectedStaffId && selectedStaffId !== 'any' ? [selectedStaffId] : [],
      notes,
      // Only include projectNumber if provided by either the modal prop or the optional input
      ...(effectiveProjectNumber ? { projectNumber: effectiveProjectNumber } : {}),
    };
    const action = await dispatch(createBookingAppointment({ businessId: selectedBusinessId, payload }));
    if (action.type.endsWith('fulfilled')) {
      // If we are rescheduling, cancel the original appointment after creating the new one
      if (rescheduleTarget && rescheduleTarget.businessId && rescheduleTarget.appointmentId) {
        const cancelAction = await dispatch(
          cancelBookingAppointment({
            businessId: rescheduleTarget.businessId,
            appointmentId: rescheduleTarget.appointmentId,
          })
        );
        if (cancelAction.type.endsWith('fulfilled')) {
          enqueueSnackbar('Original triage appointment cancelled.', { variant: 'info' });
        } else {
          enqueueSnackbar(
            cancelAction.payload || cancelAction.error?.message || 'New booking created, but failed to cancel the original appointment. Please cancel it manually.',
            { variant: 'warning' }
          );
        }
      }

      enqueueSnackbar(rescheduleTarget ? 'Triage booking rescheduled successfully.' : 'Booking created successfully.', { variant: 'success' });
      setRescheduleTarget(null);
      onClose(true);
    } else {
      enqueueSnackbar(action.payload || action.error?.message || 'Failed to create booking.', { variant: 'error' });
    }
  };

  if (!isVisible) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {initialLoading && (
          <div className={styles.initSpinner}>
            <svg width="28" height="28" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
              <circle cx="25" cy="25" r="20" fill="none" stroke="#3C96D4" strokeWidth="5" strokeLinecap="round" strokeDasharray="31.4 31.4" transform="rotate(-90 25 25)">
                <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
              </circle>
            </svg>
          </div>
        )}
        <div className={styles.header}>
          <div>
            <div className={styles.title}>Triage</div>
            <div className={styles.subTitle}>
              15 minutes{rescheduleTarget ? ` • Rescheduling: ${rescheduleTarget.calendarTitle || 'existing booking'}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className={styles.manageBtn}
              onClick={() => setShowManageTriage(true)}
              title="Manage my triage bookings"
            >
              Manage
            </button>
          <button
            className={styles.closeBtn}
            onClick={() => onClose(false)}
            aria-label="Close"
            title="Close"
          >
            ×
          </button>
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.calendarColumn}>
            <div style={{ marginBottom: '6px', fontWeight: 700 }}>Select date <span style={{ color: '#d32030' }}>*</span></div>
            <DatePicker dataName={'Date'} initialDate={selectedDate.startDate} onDatesChange={handleDateChange} keepEmpty={false} visableIcon={true} />

            <div className={styles.selectRow}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>Select staff <span style={{ color: '#d32030' }}>*</span></div>
              <select className={styles.select} value={selectedStaffId} onChange={e => setSelectedStaffId(e.target.value)}>
                <option value={''} disabled>Select staff</option>
                {staff.map(s => (
                  <option key={s.id} value={s.id}>{s.displayName || s.name || 'Staff'}</option>
                ))}
              </select>
            </div>

            <div style={{ marginTop: 10, fontWeight: 700 }}>Select time <span style={{ color: '#d32030' }}>*</span></div>
            {!selectedStaffId ? (
              <div className={styles.smallMuted} style={{ marginTop: 8 }}>Select a staff member to view available times.</div>
            ) : (
              <div className={styles.slotsContainer}>
                {slotsLoading && (
                  <div className={styles.sectionSpinner}>
                    <svg width="24" height="24" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="25" cy="25" r="20" fill="none" stroke="#3C96D4" strokeWidth="5" strokeLinecap="round" strokeDasharray="31.4 31.4" transform="rotate(-90 25 25)">
                        <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                      </circle>
                    </svg>
                  </div>
                )}
                <div className={styles.slots}>
                  {slots
                  .slice()
                  .sort((a,b) => new Date(a.start) - new Date(b.start))
                  .map(slot => {
                    const d = new Date(slot.start);
                    const label = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
                      // Disable if slot is in the past for today with buffer
                      const isPastWithBuffer = isSameDayAsSelected && (d.getTime() < (now.getTime() + bookingBufferMinutes * 60 * 1000));
                    return (
                      <button
                        key={`${slot.staffId || 'any'}-${slot.start}`}
                          className={`${styles.slotBtn} ${selectedTime === label ? styles.selected : ''} ${isPastWithBuffer ? styles.slotBtnDisabled : ''}`}
                          onClick={() => { if (!isPastWithBuffer) { setSelectedTime(label); setSelectedSlot(slot); } }}
                          disabled={isPastWithBuffer}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className={styles.smallMuted} style={{ marginTop: 8 }}>All times shown are UK time (Europe/London).</div>
          </div>

          <div>
            <div className={styles.projectRow} style={{ marginTop: 12, marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Project Number {jobNumber ? null : (<span style={{ fontWeight: 400, fontSize: 12 }}>(optional)</span>)}</div>
                <input
                  className={styles.input}
                  placeholder={jobNumber ? 'Project Number' : 'Project Number (optional)'}
                  value={projectNumberInput}
                  onChange={(e) => setProjectNumberInput((e.target.value || '').replace(/\D/g, ''))}
                  readOnly={!!jobNumber}
                  inputMode="numeric"
                />
              </div>
              {projectName && (
                <div style={{ flex: 2 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Project Name</div>
                  <input className={styles.input} placeholder={'Project Name'} value={projectName || ''} readOnly />
                </div>
              )}
            </div>

            <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 700 }}>Add your details</div>
            <div className={styles.details}>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>First and surname <span style={{ color: '#d32030' }}>*</span></div>
                <input className={styles.input} placeholder={'First and surname'} value={name} onChange={e => setName(e.target.value)} required readOnly />
              </div>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Email <span style={{ color: '#d32030' }}>*</span></div>
                <input className={styles.input} placeholder={'Email'} type='email' value={email} onChange={e => setEmail(e.target.value)} required readOnly aria-invalid={!emailValid} />
              </div>
              <div style={{ gridColumn: '1 / span 2' }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Topic<span style={{ color: '#d32030' }}> *</span></div>
                <textarea className={styles.textArea} placeholder={'Brief topic or agenda'} value={notes} onChange={e => setNotes(e.target.value)} required />
              </div>
              <div />
            </div>

              <div className={styles.footer}>
                <div className={styles.error}>
                  {!canBook ? 'Select a date, time, staff member, and enter name, email, and a topic' : ''}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    className={styles.bookBtn}
                    disabled={!canBook || bookings.loading}
                    onClick={handleBook}
                  >
                    {bookings.loading
                      ? rescheduleTarget
                        ? 'Rescheduling…'
                        : 'Booking…'
                      : rescheduleTarget
                        ? 'Confirm new time'
                        : 'Book'}
                  </button>
                </div>
              </div>
          </div>
        </div>
        {showManageTriage && (
          <TriageAppointmentsModal
            isVisible={showManageTriage}
            onClose={() => setShowManageTriage(false)}
            onReschedule={handleStartReschedule}
          />
        )}
      </div>
    </div>
  );
}


